import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { Row } from "@/libraries/wpl/foundations/src";
import AgileFooter from "@/patterns/agile-footer/src";
import { AgileHubSideNav } from "@/patterns/agile-overview-sidenav/src";
import GlobalNavigation from "@/patterns/global-nav/src";
import SoftwareDevelopmentProductNav from "@/patterns/product-navs/src/software-development-product-nav";

import { AgileOverview } from "../types";

import { useAgileOverview } from "./agile-overview.provider";
import { AgileAuthor, AgileAuthorProps } from "./sections/agile-author";
import { AgileCards } from "./sections/agile-cards";
import { AgileHero, AgileHeroProps } from "./sections/agile-hero";
import { AgileOverviewMainContent } from "./sections/main-content";
import {
    NavigationCardProps,
    NavigationCards,
    useCardProps
} from "./sections/navigation-cards";
import { ProductCTA } from "./sections/product-cta";
import { ShareUpNextBar } from "./sections/share-up-next";
import { useAgileHubPageTreeData } from "./use-agile-hub-page-tree-data";

// Override Row layout to keep sidenav and content inline
// FIXME: These nested selectors are hacks to components we own
const rowWrapperCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> .row-layout": {
        gridTemplateColumns: "repeat(12, 1fr) !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".agile-sidenav": {
        gridColumnStart: "1 !important",
        gridColumnEnd: "4 !important"
    },
    [media.below.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".agile-sidenav": {
            display: "none"
        }
    },
    "boxShadow": "0 10px 14px rgba(0,0,0,.1)",
    "paddingBlockStart": "32px",
    "paddingBlockEnd": "48px"
});

const mainContentCss = css({
    gridColumnStart: "4 !important",
    gridColumnEnd: "-1 !important",
    [media.below.sm]: {
        gridColumnStart: "1 !important",
        gridColumnEnd: "-1 !important"
    }
});

export const AgileOverviewPageTemplate = () => {
    const agileOverviewData = useAgileOverview();

    if (!agileOverviewData) {
        return null;
    }

    return <AgileOverviewPageTemplateContent {...agileOverviewData} />;
};

const AgileOverviewPageTemplateContent = (props: AgileOverview) => {
    const agileHeroProps: AgileHeroProps = {
        headerText: props.title,
        secondaryText: props.subtitle || "",
        backgroundPicture: props.bannerImage
    };

    const agileAuthorProps: AgileAuthorProps = {
        name: props.author?.name,
        biography: props.author?.shortBiography,
        pictureSrc: props.author?.authorPicture?.url,
        contentType: props.author?.authorPicture?.contentType
    };

    const agileCardsProps: NavigationCardProps[] = useCardProps(
        props?.featuredContentContainer || []
    );

    return (
        <>
            <GlobalNavigation
                isShowTryNowLink={true}
                tryNowLinkCtaButtonColor="blue"
                productKey="agile"
            />
            <SoftwareDevelopmentProductNav selectedNavLink="agile" />
            <ContentNotFoundErrorBoundary>
                <AgileHero {...agileHeroProps} />
                <div css={rowWrapperCss}>
                    <Row layout="3-9" alignItems="top">
                        <AgileHubSideNav nodes={useAgileHubPageTreeData()} />
                        <div css={mainContentCss}>
                            {props.mainCallToAction && (
                                <ProductCTA
                                    name="Kanban"
                                    {...props.mainCallToAction}
                                />
                            )}
                            <AgileOverviewMainContent
                                topicOverviewData={props}
                            />
                            {props.author && (
                                <AgileAuthor {...agileAuthorProps} />
                            )}
                            <div
                                css={css({
                                    [media.above.sm]: { display: "none" }
                                })}
                            >
                                <AgileCards
                                    headerText={props.name}
                                    cardsProps={agileCardsProps}
                                />
                            </div>
                        </div>
                    </Row>
                </div>
                <NavigationCards cardsData={props.nextBestAction} />
                <ShareUpNextBar
                    nodes={useAgileHubPageTreeData()}
                    title={props.title}
                    description={props.description}
                />
            </ContentNotFoundErrorBoundary>
            <AgileFooter />
        </>
    );
};
